import { CustomButton } from 'components/Button';
import { AuthPopup } from 'features/auth';
import CallToActionSection from 'features/marketing/components/sections/callToActionSection';
import EnterpriseSection from 'features/marketing/components/sections/enterpriseSection';
// EnterpriseResourcesSection and FaqSection will be lazy-loaded
import FeaturesSection from 'features/marketing/components/sections/featuresSection';
import HowItsWorkSection from 'features/marketing/components/sections/howItsWork';
// Below-the-fold sections will be lazy-loaded
import SeamlessIntegrationSection from 'features/marketing/components/sections/seamlessIntegrationSection';
import SolutionsSection from 'features/marketing/components/sections/solutionsSection';
import TrustedCASection from 'features/marketing/components/sections/trustedCASection';
import WhyChooseLottoRecoSection from 'features/marketing/components/sections/whyChooseLottoRecoSection';
import { DemoRequestForm, useDemoRequest } from 'components/DemoRequest';
import ChatSupport from 'components/ChatSupport';
import { lazy, Suspense, useState, useRef, useEffect } from "react";
import { Dialog, DialogContent } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Helmet } from 'react-helmet-async';

// Lazy-loaded below-the-fold sections
const PricingSection = lazy(() => import('features/marketing/components/sections/pricingSection'));
const ProductPlanSection = lazy(() => import('features/marketing/components/sections/planSection'));
const FaqSection = lazy(() => import('features/marketing/components/sections/faqSection'));
const EnterpriseResourcesSection = lazy(() => import('features/marketing/components/sections/enterpriseResourcesSection'));

export default function LandingPage() {
  const [openLogin, setOpenLogin] = useState<boolean>(false);
  const [isSignupMode, setIsSignupMode] = useState<boolean>(false);
  const [openVideoPopup, setOpenVideoPopup] = useState<boolean>(false);
  const { isOpen: isDemoOpen, isPending: isDemoPending, handleOpen: handleDemoOpen, handleClose: handleDemoClose, handleSubmit: handleDemoSubmit } = useDemoRequest();
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  function handleOpenLogin(value: string) {
    setOpenLogin(true);
    setIsSignupMode(value === "signUp" ? true : false);
  }

  const handleVideoClick = () => {
    setOpenVideoPopup(true);
    setVideoError(null);
    setVideoLoading(true);
  };

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const video = e.currentTarget;
    const error = video.error;
    let errorMessage = 'Unknown video error';
    
    if (error) {
      switch (error.code) {
        case error.MEDIA_ERR_ABORTED:
          errorMessage = 'Video loading was aborted';
          break;
        case error.MEDIA_ERR_NETWORK:
          errorMessage = 'Network error occurred while loading video';
          break;
        case error.MEDIA_ERR_DECODE:
          errorMessage = 'Video decoding error';
          break;
        case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMessage = 'Video format not supported';
          break;
      }
    }
    
    console.error('Video error:', errorMessage, error);
    setVideoError(errorMessage);
    setVideoLoading(false);
  };

  const handleVideoLoadStart = () => {
    console.log('Video loading started');
    setVideoLoading(true);
  };

  const handleVideoCanPlay = () => {
    console.log('Video can play');
    setVideoLoading(false);
    setVideoError(null);
  };

  const handleVideoLoadedData = () => {
    console.log('Video data loaded');
  };

  const handleVideoLoadedMetadata = () => {
    console.log('Video metadata loaded');
  };
  return (
    <>
      <Helmet defer={false}>
        <title>Lottery Management System | Automated Reconciliation Software | LottoReco</title>
        <meta name="description" content="Complete lottery management system for convenience stores. Automated reconciliation software reduces daily tasks from 45 minutes to 5 minutes with 99.9% accuracy." />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="en-US" />
        <link rel="canonical" href={'https://lottoreco.com/'} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Lottery Management System | Automated Reconciliation Software | LottoReco" />
        <meta property="og:description" content="Complete lottery management system for convenience stores. Automated reconciliation software reduces daily tasks from 45 minutes to 5 minutes with 99.9% accuracy." />
        <meta property="og:url" content={'https://lottoreco.com/'} />
        <meta property="og:image" content={typeof window !== 'undefined' ? `${window.location.origin}/og-image.jpg` : 'https://lottoreco.com/og-image.jpg'} />
        <meta property="og:site_name" content="LottoReco" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Lottery Management System | Automated Reconciliation Software | LottoReco" />
        <meta name="twitter:description" content="Complete lottery management system for convenience stores. Automated reconciliation software reduces daily tasks from 45 minutes to 5 minutes with 99.9% accuracy." />
        <meta name="twitter:image" content={typeof window !== 'undefined' ? `${window.location.origin}/og-image.jpg` : 'https://lottoreco.com/og-image.jpg'} />
        <meta name="twitter:site" content="@lottoreco" />
      </Helmet>

      <div className="width-100">
        {openLogin && (
            <AuthPopup  open={openLogin} handleClose={() => setOpenLogin(false)} isSignupMode={isSignupMode} />
        )}
      </div>

      <div className="main-content d-flex justify-center items-center">
        <div className="custom-container d-flex items-center width-100">
          <div className="main-left-sub-content">
            <h1>Complete Lottery Management System for Convenience Stores</h1>
            <p className="trust-line">
              Trusted by US convenience stores to reconcile lottery sales in under 5 minutes.
            </p>
            <p>
              Our comprehensive <a href="/lottery-reconciliation-software">lottery management system</a> helps convenience stores track every lottery ticket, scratch-off, and scratcher inventory with ease. 
              This complete <a href="/lottery-reconciliation-software">lottery management solution</a> prevents employee theft, reduces reconciliation time from 45 minutes to 5 minutes with automated 
              <a href="/lottery-reconciliation-software">lottery auditing software</a>, and manages your instant ticket inventory in minutes – no tech experience needed. Our <a href="/lottery-reconciliation-software">lottery management system</a> works with all state lottery systems, 
              with enhanced <a href="/california-lottery-compliance">compliance features for California</a>.
            </p>

            <CustomButton
              className="button-base button-compact get-started-btn"
              onClick={() => {
                handleOpenLogin("signUp");
              }}
            >
              Get Free Lottery Reconciliation Demo
            </CustomButton>
            
            <CustomButton
              className="button-base button-compact demo-request-btn"
              onClick={handleDemoOpen}
              style={{
                backgroundColor: 'transparent',
                border: '2px solid #2c6ca0',
                color: '#2c6ca0',
                marginTop: '12px',
              }}
            >
              Get a free demo link sent to your inbox
            </CustomButton>
            
            <CustomButton
              className="button-base button-compact"
              onClick={() => window.location.href = '/enterprise-solutions'}
              style={{
                backgroundColor: 'transparent',
                border: '2px solid #28a745',
                color: '#28a745',
                marginTop: '12px',
                fontWeight: '500',
              }}
            >
              <a href="/enterprise-solutions" style={{ color: 'inherit', textDecoration: 'none' }}>
                Enterprise Lottery Management Solution →
              </a>
            </CustomButton>
          </div>
          <div className="main-right-sub-content">
            <div 
              className="video-play-button"
              onClick={handleVideoClick}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: 'translateY(70px)'
              }}>
                <div style={{
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  textAlign: 'center',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  backdropFilter: 'blur(10px)'
                }}>
                  Watch 1-Minute Demo
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FeaturesSection />
      <SolutionsSection />
      <HowItsWorkSection />
      <WhyChooseLottoRecoSection />
      <EnterpriseSection />
      <SeamlessIntegrationSection />
      <Suspense fallback={null}>
        <PricingSection />
      </Suspense>
      <TrustedCASection />
      <Suspense fallback={null}>
        <ProductPlanSection
          buttonClickHandler={() => {
            handleOpenLogin("signUp");
          }}
        />
      </Suspense>
      <CallToActionSection
        buttonClickHandler={() => {
          handleOpenLogin("signUp");
        }}
      />
      <Suspense fallback={null}>
        <FaqSection />
      </Suspense>
      <Suspense fallback={null}>
        <EnterpriseResourcesSection />
      </Suspense>
      
       {/* Video Popup */}
      <Dialog
         open={openVideoPopup}
         onClose={() => setOpenVideoPopup(false)}
         maxWidth={false}
         sx={{
           '& .MuiDialog-paper': {
             backgroundColor: 'transparent',
             boxShadow: 'none',
            maxWidth: 'none',
            margin: { xs: 0, sm: 0 }
           }
         }}
         BackdropProps={{
           onClick: () => setOpenVideoPopup(false),
           sx: {
             backgroundColor: 'rgba(0, 0, 0, 0.8)'
           }
         }}
       >
        <DialogContent 
          sx={{ 
            padding: { xs: '0 16px', sm: 0 },
            backgroundColor: 'transparent',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 0,
            '&.MuiDialogContent-root': {
              margin: 0,
              padding: { xs: '0 16px', sm: 0 }
            }
          }}
         >
           <div 
             style={{ 
               position: 'relative', 
               display: 'flex',
               justifyContent: 'center',
               alignItems: 'center'
             }}
             onClick={(e) => e.stopPropagation()}
           >
            {videoLoading && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'white',
                fontSize: '18px',
                zIndex: 10
              }}>
                Loading video...
              </div>
            )}
            
            {videoError && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'red',
                fontSize: '16px',
                textAlign: 'center',
                zIndex: 10,
                padding: '20px',
                borderRadius: '8px'
              }}>
                <p>Error: {videoError}</p>
                <a 
                  href="https://lottoreco.com/downloads/introduction.mp4" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: 'white', textDecoration: 'underline' }}
                >
                  Click here to download the video
                </a>
              </div>
            )}
            
            <video
              ref={videoRef}
              controls
              autoPlay
              playsInline
              preload="metadata"
               style={{
                 borderRadius: '8px',
                 width: '100%',
                 height: 'auto',
                 maxWidth: '760px',
                 objectFit: 'contain',
                 opacity: videoLoading ? 0.5 : 1,
                 backgroundColor: '#000'
               }}
              onError={handleVideoError}
              onLoadStart={handleVideoLoadStart}
              onLoadedData={handleVideoLoadedData}
              onCanPlay={handleVideoCanPlay}
              onLoadedMetadata={handleVideoLoadedMetadata}
            >
              <source src="https://lottoreco.com/downloads/introduction.mp4" type="video/mp4" />
              <p>Your browser does not support the video tag. <a href="https://lottoreco.com/downloads/introduction.mp4">Download the video</a> instead.</p>
            </video>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Demo Request Form */}
      <DemoRequestForm
        open={isDemoOpen}
        onClose={handleDemoClose}
        onSubmit={handleDemoSubmit}
        isPending={isDemoPending}
      />
      
      {/* Chat Support */}
      <ChatSupport />
    </>
  );
}
